// App shell: topbar row + (sidebar + main) row. Both sidebar and main scroll
// independently. Auth still gates the whole shell.
//
// On mobile (<768px) the sidebar becomes a slide-in drawer over the main
// area; the topbar toggle flips between collapsed/expanded on desktop and
// open/closed on mobile.
//
// Shell height is `100dvh`, NOT `h-screen` (`100vh`). On a mobile browser
// `100vh` is TALLER than the visible area, because it excludes the collapsing
// address bar. Since this container is `overflow-hidden`, that surplus is
// clipped and unreachable: `<main>` scrolls internally, but its bottom edge sits
// below the fold, so the last strip of content can never be scrolled into view.
// Steven hit exactly that on 2026-08-04, reaching the end of a grid on his phone
// with the totals footer sliced in half and no way to scroll further.
// `dvh` tracks the visible viewport, so the shell's bottom edge and the screen's
// bottom edge are the same line. Already used elsewhere in this app
// (pages/stores/HqPhotos.tsx) and supported by every browser this app runs in.

import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { AuthGate } from './AuthGate';
import { HelpPanel } from '@/components/HelpPanel';
import { ReportIssueModal } from '@/components/ReportIssueModal';
import { ChatBubbleMount } from './ChatBubbleMount';
import { useCollapsedSidebar } from '@/lib/useCollapsedSidebar';
import { useIsMobile } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { isStoreOnlyUser } from '@/lib/useStores';
import {
  captureCockpitScreenshot,
  cockpitToolLabelForPath,
  type SupportScreenshot,
} from '@/lib/supportIssue';
import { getReportPageState, type ReportPageStateEntry } from '@/lib/reportPageState';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { useRecordReportVisits } from '@/lib/reportRecents';

type ReportDraft = {
  tool?: string;
  route: string;
  screenshot: SupportScreenshot | null;
  screenshotAttempted: boolean;
  pageState: ReportPageStateEntry[];
};

export function Layout() {
  const [collapsed, setCollapsed] = useCollapsedSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [reportDraft, setReportDraft] = useState<ReportDraft | null>(null);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useSession();
  useDocumentTitle();
  useRecordReportVisits();

  // Store-only accounts (store staff signing in with Google) get a dedicated
  // mobile shell: no department sidebar, just the topbar and their store
  // tools. The store home at /stores is their landing page.
  const storeOnly = isStoreOnlyUser(user?.permissions);

  // Auto-close the mobile drawer when the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // `?help=1` on any page opens its help article on arrival, so a What's new
  // entry or an email can link straight to a page with its article showing.
  // The flag is removed from the address once used, so a refresh does not
  // reopen the panel.
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('help') !== '1') return;
    setHelpOpen(true);
    params.delete('help');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  const onToggle = () => {
    if (isMobile) setMobileOpen((v) => !v);
    else setCollapsed(!collapsed);
  };

  const openReportIssue = async ({ closeHelpFirst = false }: { closeHelpFirst?: boolean } = {}) => {
    const route = `${location.pathname}${location.search}`;
    const tool = cockpitToolLabelForPath(location.pathname);
    // Read the page's published state immediately, before the closeHelpFirst
    // delay below, so it reflects what was on screen at the moment of the
    // click rather than whatever the page has moved to (or unmounted from)
    // by the time the screenshot resolves.
    const pageState = getReportPageState();
    if (closeHelpFirst) {
      setHelpOpen(false);
      await new Promise((resolve) => window.setTimeout(resolve, 250));
    }
    const screenshot = await captureCockpitScreenshot(tool);
    setReportDraft({ tool, route, screenshot, screenshotAttempted: true, pageState });
  };

  if (storeOnly) {
    return (
      <AuthGate>
        <div className="flex h-[100dvh] flex-col overflow-hidden bg-amp-cream text-amp-ink dark:bg-amp-dark-bg dark:text-amp-dark-ink">
          <Header
            collapsed={false}
            onToggleCollapsed={() => {}}
            hideSidebarToggle
            onHelpOpen={() => setHelpOpen(true)}
            onReportIssueOpen={() => void openReportIssue()}
          />
          <main data-cockpit-main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
            <Outlet />
          </main>
          <HelpPanel
            open={helpOpen}
            onClose={() => setHelpOpen(false)}
            onReportIssue={() => void openReportIssue({ closeHelpFirst: true })}
          />
          <ReportIssueModal
            open={reportDraft !== null}
            initialTool={reportDraft?.tool}
            route={reportDraft?.route ?? ''}
            screenshot={reportDraft?.screenshot ?? null}
            screenshotAttempted={reportDraft?.screenshotAttempted ?? false}
            pageState={reportDraft?.pageState}
            onClose={() => setReportDraft(null)}
          />
          <ChatBubbleMount />
        </div>
      </AuthGate>
    );
  }

  return (
    <AuthGate>
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-amp-cream text-amp-ink dark:bg-amp-dark-bg dark:text-amp-dark-ink">
        <Header
          collapsed={collapsed}
          onToggleCollapsed={onToggle}
          onHelpOpen={() => setHelpOpen(true)}
          onReportIssueOpen={() => void openReportIssue()}
        />
        <div className="relative flex min-h-0 flex-1">
          <Sidebar
            collapsed={collapsed}
            isMobile={isMobile}
            mobileOpen={mobileOpen}
            onExpand={() => setCollapsed(false)}
          />
          {isMobile && mobileOpen ? (
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="fixed inset-x-0 bottom-0 z-30 bg-black/30"
              style={{ top: 56 }}
            />
          ) : null}
          <main data-cockpit-main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8 lg:px-10">
            <Outlet />
          </main>
        </div>
        <HelpPanel
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          onReportIssue={() => void openReportIssue({ closeHelpFirst: true })}
        />
        <ReportIssueModal
          open={reportDraft !== null}
          initialTool={reportDraft?.tool}
          route={reportDraft?.route ?? ''}
          screenshot={reportDraft?.screenshot ?? null}
          screenshotAttempted={reportDraft?.screenshotAttempted ?? false}
          pageState={reportDraft?.pageState}
          onClose={() => setReportDraft(null)}
        />
        <ChatBubbleMount />
      </div>
    </AuthGate>
  );
}
